// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import FeedPostPreviews, {type FeedPostPreview} from '@app/features/feed/state/FeedPostPreviews';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {MessageAttachmentFlags} from '@fluxer/constants/src/ChannelConstants';
import type {FeedPostResponse} from '@fluxer/schema/src/domains/feed/FeedResponseSchemas';

const logger = new Logger('FeedPostPreviewCommands');

const PREVIEW_TEXT_LENGTH = 300;
const HIDDEN_IMAGE_FLAGS = MessageAttachmentFlags.IS_SPOILER | MessageAttachmentFlags.CONTAINS_EXPLICIT_MEDIA;

function previewOf(post: FeedPostResponse): FeedPostPreview {
	const image = post.attachments.find(
		(attachment) => attachment.content_type?.startsWith('image/') && (attachment.flags & HIDDEN_IMAGE_FLAGS) === 0,
	);
	return {
		author: post.author,
		text: (post.content ?? '').slice(0, PREVIEW_TEXT_LENGTH),
		imageUrl: image?.proxy_url ?? null,
	};
}

// One request per post per session, whatever number of cards show it; a refusal of any kind is not asked again.
export async function loadPostPreview(postId: string): Promise<void> {
	const session = FeedPostPreviews.beginLoad(postId);
	if (session === null) return;
	let preview: FeedPostPreview | null = null;
	try {
		const response = await http.get<FeedPostResponse>(Endpoints.FEED_POST(postId));
		if (response.body) preview = previewOf(response.body);
	} catch (error) {
		logger.warn(`Failed to load the preview of post ${postId}:`, error);
	}
	FeedPostPreviews.handleLoaded(session, postId, preview);
}
